/**
 * AISuggestionModal — "Ask AI for Suggestion" popup for Fix & Continue.
 *
 * Replaces the former right-side AI drawer (AIFixPanel) with a centered modal
 * that is visually consistent with AI Code Explanation (AIResponseModal).
 *
 * JSON CONTRACT
 * ─────────────
 * The default prompt instructs Claude to return ONLY a JSON object:
 *   {
 *     "action": "CUSTOM_EDIT",
 *     "file": "<FileName.java>",
 *     "start_line": <int>,
 *     "end_line": <int>,
 *     "replacement_text": "<corrected Java lines>",
 *     "confidence": <0.0–1.0>,
 *     "explanation": "<one sentence describing the fix>"
 *   }
 * If the AI response cannot be parsed as this shape, a parse-error message
 * is shown and the user can click Regenerate or edit the prompt.
 * Claude may wrap the JSON in ```json...``` fences — those are stripped before parsing.
 *
 * APPLY FLOW
 * ──────────
 * Apply        → parse JSON → replaceLineRange(start, end, text) in Monaco
 *              → applyFix() → upsertDecision(CUSTOM_EDIT) → Changes tab entry
 * Apply & Re-run → same + executeRerun() for compile/package gate
 *
 * Reuses .ai-modal-* CSS classes from AIResponseModal.css for consistent look.
 * Additional overrides are in AISuggestionModal.css (imported after).
 */

import { useState, useMemo, useCallback } from 'react'
import {
  X, Sparkles, Copy, Check, RefreshCw, Send,
  Loader2, Play, AlertTriangle, Code2, Clock,
  Maximize2, Minimize2
} from 'lucide-react'
import Editor, { DiffEditor } from '@monaco-editor/react'
import DraggableModal from './DraggableModal'
import './AIResponseModal.css'
import './AISuggestionModal.css'
import { extractAIPatch } from '../../utils/aiJsonParser'

// Shared utility — handles all model response formats (raw JSON, fenced, embedded)
const parsePatch = (result) => extractAIPatch(result)

// ─────────────────────────────────────────────────────────────────────────────
// AISuggestionModal
// ─────────────────────────────────────────────────────────────────────────────
export default function AISuggestionModal({
  isOpen,
  onClose,
  issue,           // { summary, javaLocation: { file, line }, snippet, fingerprint }
  gateType,        // 'compile' | 'package' | 'structural'
  originalCode,    // code context around issue line (for DiffEditor)
  loading,         // bool — AI request in flight
  result,          // { response, code, confidence, duration_ms } from customPromptAI
  error,           // string | null — API-level error
  applied,         // bool — patch already applied to editor (disables Apply buttons)
  saving,          // bool — PUT /editor/file in-flight (Apply & Re-run save step)
  rerunning,       // bool — gate re-run in progress
  prompt,          // controlled prompt textarea value
  onPromptChange,  // callback(newPromptString)
  onGenerate,      // () → trigger AI call (calls customPromptAI in parent)
  onApply,         // (patchObj) → apply parsed patch to editor + persist decision
  onApplyAndRerun, // (patchObj) → apply + trigger gate re-run
  onApplyAsStub,   // () → fall back: apply structure-aware TODO stub instead of AI patch
  onCopy,          // () → called after clipboard write (parent shows toast)
  onRegenerate,    // () → re-trigger generation
}) {
  const [copied,       setCopied]       = useState(false)
  const [showDiff,     setShowDiff]     = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [modalSize,    setModalSize]    = useState({ width: 860, height: null })

  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startY = e.clientY
    const startW = modalSize.width
    const startH = modalSize.height || 520
    const onMove = (ev) => {
      setModalSize({
        width:  Math.max(600, Math.min(1400, startW + (ev.clientX - startX))),
        height: Math.max(400, Math.min(window.innerHeight * 0.92, startH + (ev.clientY - startY))),
      })
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup',   onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup',   onUp)
  }, [modalSize])

  // Parse the structured JSON from the AI response on every result change
  const patchObj = useMemo(() => parsePatch(result), [result])

  if (!isOpen) return null

  const hasResult     = Boolean(result)
  const canApply      = Boolean(patchObj) && !applied && !saving && !rerunning && !loading
  const showParseError = hasResult && !loading && !patchObj && !error

  const handleCopy = () => {
    const text = patchObj?.replacement_text || result?.code || result?.response || ''
    if (text) navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    onCopy?.()
  }

  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <DraggableModal
        className={`ai-modal ais-modal${isFullscreen ? ' ais-fullscreen' : ''}`}
        wrapperStyle={isFullscreen ? {} : {
          width: modalSize.width,
          ...(modalSize.height ? { height: modalSize.height } : {}),
        }}
        onClose={onClose}
        onClick={e => e.stopPropagation()}
      >

        {/* ── Header ── */}
        <div className="ai-modal-header">
          <div className="ai-modal-title">
            <Sparkles size={18} />
            <span>AI Fix Suggestion</span>
          </div>

          {/* Issue context chip: filename:line · gateType */}
          {issue && (
            <div className="ais-context-chip">
              <code>{issue.javaLocation?.file || 'unknown'}</code>
              {issue.javaLocation?.line && <span>:{issue.javaLocation.line}</span>}
              <span className="ais-gate-tag">{gateType}</span>
            </div>
          )}

          <button
            className="ai-modal-icon-btn"
            onClick={() => setIsFullscreen(f => !f)}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button className="ai-modal-close" onClick={onClose} disabled={saving} title={saving ? 'Save in progress' : 'Close'}>
            <X size={18} />
          </button>
        </div>

        {/* ── Content ── */}
        <div className="ai-modal-content">

          {/* Issue error summary */}
          {issue?.summary && (
            <div className="ais-issue-banner">
              <AlertTriangle size={12} />
              <span>{issue.summary}</span>
            </div>
          )}

          {/* Prompt textarea + Generate button */}
          <div className="ais-prompt-section">
            <textarea
              className="ais-prompt-textarea"
              value={prompt}
              onChange={e => onPromptChange(e.target.value)}
              placeholder="Edit the prompt or describe the fix you need…"
              disabled={loading}
              rows={4}
              onKeyDown={e => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault()
                  onGenerate()
                }
              }}
            />
            <button
              className="ai-modal-btn primary ais-generate-btn"
              onClick={onGenerate}
              disabled={loading || !prompt.trim()}
              title="Cmd/Ctrl+Enter to generate"
            >
              {loading
                ? <><Loader2 size={13} className="spinning" /> Generating…</>
                : <><Send size={13} /> Generate</>
              }
            </button>
          </div>

          {/* API-level error */}
          {error && (
            <div className="ais-error">
              <AlertTriangle size={13} />
              <span>{error}</span>
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="ai-modal-loading">
              <Loader2 size={28} className="spinning" />
              <p>Generating fix suggestion…</p>
              <span>Claude is analyzing the compile error</span>
            </div>
          )}

          {/* Parse error: response received but does not match expected JSON shape */}
          {showParseError && (
            <div className="ais-parse-error">
              <AlertTriangle size={14} />
              <div>
                <strong>Response did not match expected JSON format.</strong>
                <p>Click Regenerate to retry. The prompt already asks for JSON-only output.</p>
              </div>
            </div>
          )}

          {/* Raw fallback text shown alongside parse error */}
          {showParseError && (result?.code || result?.response) && (
            <div className="ai-explanation ais-raw-fallback">
              <pre>{result.code || result.response}</pre>
            </div>
          )}

          {/* ── Structured patch result ── */}
          {!loading && patchObj && (
            <div className="ai-modal-response">

              {/* Explanation from Claude */}
              {patchObj.explanation && (
                <div className="ai-explanation ais-explanation">
                  <pre>{patchObj.explanation}</pre>
                </div>
              )}

              {/* Metadata row: confidence · timing · line range */}
              <div className="ais-meta-row">
                {patchObj.confidence !== undefined && (
                  <span className="confidence-badge">
                    {Math.round(patchObj.confidence * 100)}% confidence
                  </span>
                )}
                {result?.duration_ms && (
                  <span className="ais-duration">{(result.duration_ms / 1000).toFixed(1)}s</span>
                )}
                {patchObj.start_line && (
                  <span className="ais-lines">
                    line{patchObj.end_line && patchObj.end_line !== patchObj.start_line ? 's' : ''}{' '}
                    {patchObj.start_line}{patchObj.end_line && patchObj.end_line !== patchObj.start_line ? `–${patchObj.end_line}` : ''}
                  </span>
                )}
              </div>

              {/* Code / Diff toggle + Monaco preview */}
              <div className="ai-generated-code">
                <div className="code-header">
                  <Code2 size={13} />
                  <span>Suggested replacement</span>
                  {/* Diff toggle only visible when we have original context */}
                  {originalCode && (
                    <div className="ais-toggle">
                      <button
                        className={`ais-toggle-btn${!showDiff ? ' active' : ''}`}
                        onClick={() => setShowDiff(false)}
                      >
                        Code
                      </button>
                      <button
                        className={`ais-toggle-btn${showDiff ? ' active' : ''}`}
                        onClick={() => setShowDiff(true)}
                      >
                        Diff
                      </button>
                    </div>
                  )}
                </div>
                <div className="code-editor-wrapper">
                  {showDiff && originalCode ? (
                    <DiffEditor
                      height="280px"
                      language="java"
                      theme="vs-dark"
                      original={originalCode}
                      modified={patchObj.replacement_text}
                      options={{
                        readOnly: true,
                        renderSideBySide: false,
                        minimap: { enabled: false },
                        fontSize: 12,
                        automaticLayout: true,
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        ignoreTrimWhitespace: false,
                      }}
                    />
                  ) : (
                    <Editor
                      height="280px"
                      language="java"
                      theme="vs-dark"
                      value={patchObj.replacement_text}
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        fontSize: 12,
                        lineNumbers: 'on',
                        automaticLayout: true,
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        padding: { top: 8, bottom: 8 },
                      }}
                    />
                  )}
                </div>
              </div>

            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="ai-modal-footer">
          <button
            className="ai-modal-btn secondary"
            onClick={handleCopy}
            disabled={!hasResult}
            title="Copy to clipboard"
          >
            {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy</>}
          </button>

          <div className="ai-modal-footer-right">
            <button
              className="ai-modal-btn secondary"
              onClick={onRegenerate}
              disabled={loading}
              title="Re-generate suggestion"
            >
              <RefreshCw size={13} />
              Regenerate
            </button>

            <button
              className="ai-modal-btn primary"
              onClick={() => onApply(patchObj)}
              disabled={!canApply}
              title={!patchObj ? 'Generate a suggestion first' : applied ? 'Already applied' : 'Apply fix to Java editor'}
            >
              <Check size={13} />
              {applied ? 'Applied ✓' : 'Apply'}
            </button>

            <button
              className="ai-modal-btn ais-apply-rerun"
              onClick={() => onApplyAndRerun(patchObj)}
              disabled={!canApply}
              title="Apply fix, save overlay, and re-run the gate check"
            >
              {saving
                ? <><Loader2 size={13} className="spinning" /> Saving…</>
                : rerunning
                  ? <><Loader2 size={13} className="spinning" /> Re-running…</>
                  : <><Play size={13} /> Apply &amp; Re-run</>
              }
            </button>

            {onApplyAsStub && (
              <button
                className="ai-modal-btn ais-apply-stub"
                onClick={onApplyAsStub}
                disabled={applied || saving || rerunning || loading}
                title="Ignore AI suggestion — apply a structure-aware TODO stub instead (safe fallback)"
              >
                <Clock size={13} />
                Apply as Stub
              </button>
            )}

            <button className="ai-modal-btn" onClick={onClose} disabled={saving} title={saving ? 'Save in progress — please wait' : undefined}>
              {saving ? 'Saving…' : 'Close'}
            </button>
          </div>
        </div>

        {!isFullscreen && (
          <div className="ais-resize-handle" onMouseDown={handleResizeMouseDown} />
        )}

      </DraggableModal>
    </div>
  )
}
